import { useEffect, useState } from 'react';
import throttle from 'lodash/throttle';
import flatten from 'lodash/flatten';
import bboxPolygon from '@turf/bbox-polygon';
import type {
  BBox,
  Feature,
  MultiPolygon,
  Polygon,
  Properties,
} from '@turf/helpers';
import union from '@turf/union';

import { normalizeBbox, executeWhenMapLoaded } from '../helpers/utils';

const SAMPLING_RATE = 100;

export const useMapBoundsPolygon = (map: mapboxgl.Map | null) => {
  const [polygon, setPolygon] = useState<Feature<
    Polygon | MultiPolygon,
    Properties
  > | null>(null);

  useEffect(() => {
    if (!map) {
      return;
    }

    // don't use debounce as it let's user wait for the end ov the movement to update pin position, better use throttle and tweak sample rate
    const handleMapMove = throttle(() => {
      const boundsBbox = normalizeBbox(flatten(map.getBounds().toArray()));
      const [bottomLeftLng, bottomLeftLat, topRightLng, topRightLat] =
        boundsBbox;

      // all the polygons should be in -180;180 longitude range to be used in further operations with other data, such as waterpolygon which,
      // so it should be split several times if needed, basically, split it in 2 polygons that are then mapped to be in range -180;180 if needed
      let polygon: Feature<Polygon | MultiPolygon, Properties> | null = null;
      if (bottomLeftLng < -180 && topRightLng > -180) {
        polygon = union(
          bboxPolygon([
            bottomLeftLng + 360,
            bottomLeftLat,
            180,
            topRightLat,
          ] as BBox),
          bboxPolygon([-180, bottomLeftLat, topRightLng, topRightLat] as BBox),
        );
      } else if (bottomLeftLng < 180 && topRightLng > 180) {
        polygon = union(
          bboxPolygon([bottomLeftLng, bottomLeftLat, 180, topRightLat] as BBox),
          bboxPolygon([
            -180,
            bottomLeftLat,
            topRightLng - 360,
            topRightLat,
          ] as BBox),
        );
      } else {
        polygon = bboxPolygon(boundsBbox as BBox);
      }

      setPolygon(polygon);
    }, SAMPLING_RATE);

    map.on('move', handleMapMove);
    executeWhenMapLoaded(map, handleMapMove);

    return () => {
      map.off('move', handleMapMove);
    };
  }, [map]);

  return polygon;
};
